import { Close, Search } from '@mui/icons-material';
import {
  FormControl,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CurrentSelectedOptionBuilder = (companies, userCompany) => {
  const currentCompany = Object.values(companies).find(
    c => c.companyId === userCompany.current
  );

  const storeNumber = currentCompany.storeNumber
    ? `${currentCompany.storeNumber} -`
    : '';
  return (
    <MenuItem
      sx={{ display: 'none' }}
      key={`${currentCompany.companyId}h`}
      value={currentCompany.companyId}
    >
      {`${storeNumber} ${currentCompany.name}`}
    </MenuItem>
  );
};

const CompanySelect = ({
  onChange,
  companies,
  userCompany,
  locationDisplay,
  placeholder,
}) => {
  const { t } = useTranslation('global');
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState('');

  const selectedCompany = useRef(null);

  useEffect(() => {
    setCompany(userCompany);
    selectedCompany.current = userCompany;
  }, [userCompany]);

  const handleOnChange = e => {
    selectedCompany.current = e.target.value;
    onChange(e.target.value);
    setCompany(e.target.value);
  };

  const handleClearText = () => {
    setSearch('');
  };

  const companiesWithContracts = companies.filter(c => c.hasContracts);

  const displayOptions = useMemo(() => {
    if (search) {
      return companiesWithContracts.filter(c => {
        const name = c.name.toLowerCase();
        const number = c.storeNumber;
        return number
          ? name.includes(search.toLowerCase()) || number.includes(search)
          : name.includes(search.toLowerCase());
      });
    } else {
      return companiesWithContracts;
    }
  }, [search, companiesWithContracts]);

  return (
    locationDisplay && (
      <Stack>
        <FormControl fullWidth>
          <Select
            MenuProps={{
              autoFocus: false,
              sx: {
                '.MuiPaper-root': {
                  width: '340px',
                },
                '.MuiList-root': {
                  paddingTop: '0px',
                  maxHeight: '240px',
                  overflow: 'scroll',
                },
              },
            }}
            sx={{
              maxWidth: { xs: '148px', sm: 'none' },
              color: theme => theme.palette.primary.main,
              ':hover': { color: theme => theme.palette.primary.dark },
              '.MuiOutlinedInput-notchedOutline': { border: 0 },
              '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                {
                  border: 0,
                },
              '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                {
                  border: 0,
                },
              '.MuiSvgIcon-root': {
                color: theme => theme.palette.primary.main,
              },
              ':hover .MuiSvgIcon-root': {
                color: theme => theme.palette.primary.dark,
              },
              '.Mui-disabled': {
                color: theme => `${theme.palette.action.disabled} !important`,
              },
            }}
            autoFocus={false}
            onChange={handleOnChange}
            disabled={companiesWithContracts.length <= 1}
            inputProps={{
              sx: { padding: '0px' },
            }}
            onClose={handleClearText}
            displayEmpty={true}
            value={company}
          >
            <MenuItem sx={{ display: 'none' }} value=""></MenuItem>
            <ListSubheader
              sx={{
                backgroundColor: theme => theme.palette.grey[100],
                paddingTop: 1,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                name="search"
                autoFocus
                onKeyDown={e => {
                  e.key === 'Enter' && e.preventDefault();
                  e.key !== 'Escape' && e.stopPropagation();
                }}
                placeholder={placeholder}
                sx={{
                  backgroundColor: theme => theme.palette.background.paper,
                  borderColor: theme => theme.palette.divider,
                  '.MuiOutlinedInput-root.Mui-focused': {
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: theme => theme.palette.grey[400],
                    },
                  },
                }}
                inputProps={{ 'data-testid': 'searchSelect' }}
                InputProps={{
                  'aria-label': `${placeholder}`,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  ...(search !== '' && {
                    endAdornment: (
                      <IconButton
                        aria-label="clear-text"
                        onClick={handleClearText}
                        edge="end"
                      >
                        <Close />
                      </IconButton>
                    ),
                  }),
                }}
                size="small"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </ListSubheader>
            {company &&
              selectedCompany.current &&
              CurrentSelectedOptionBuilder(
                companiesWithContracts,
                selectedCompany
              )}
            {displayOptions.map((c, i) => {
              const storeNumber = c.storeNumber ? `${c.storeNumber} -` : '';
              return (
                <MenuItem key={c.companyId + i} value={c.companyId}>
                  <Typography
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      maxWidth: '320px',
                    }}
                  >{`${storeNumber} ${c.name}`}</Typography>
                </MenuItem>
              );
            })}
            {!displayOptions.length && (
              <ListItem>
                <ListItemText
                  primary={t('noOptions')}
                  sx={{ color: theme => theme.palette.text.disabled }}
                />
              </ListItem>
            )}
          </Select>
        </FormControl>
      </Stack>
    )
  );
};

export default CompanySelect;
